import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Status color mappings
export const statusColors = {
  new_lead: {
    bg: "bg-[#DBEAFE]",
    text: "text-[#1E40AF]",
    border: "border-[#BFDBFE]",
    label: "New Lead"
  },
  contacted: {
    bg: "bg-[#E0E7FF]",
    text: "text-[#3730A3]",
    border: "border-[#C7D2FE]",
    label: "Contacted"
  },
  follow_up: {
    bg: "bg-[#F3E8FF]",
    text: "text-[#6B21A8]",
    border: "border-[#E9D5FF]",
    label: "Follow Up"
  },
  due: {
    bg: "bg-[#FEF9C3]",
    text: "text-[#854D0E]",
    border: "border-[#FEF08A]",
    label: "Due"
  },
  overdue: {
    bg: "bg-[#FFEDD5]",
    text: "text-[#9A3412]",
    border: "border-[#FED7AA]",
    label: "Overdue"
  },
  delayed: {
    bg: "bg-[#FEE2E2]",
    text: "text-[#991B1B]",
    border: "border-[#FECACA]",
    label: "Delayed"
  },
  no_show: {
    bg: "bg-[#450A0A]",
    text: "text-[#FEE2E2]",
    border: "border-[#7F1D1D]",
    label: "No Show"
  },
  booked: {
    bg: "bg-[#D1FAE5]",
    text: "text-[#065F46]",
    border: "border-[#A7F3D0]",
    label: "Booked"
  },
  converted: {
    bg: "bg-[#D1FAE5]",
    text: "text-[#065F46]",
    border: "border-[#A7F3D0]",
    label: "Converted"
  },
  active: {
    bg: "bg-[#D1FAE5]",
    text: "text-[#065F46]",
    border: "border-[#A7F3D0]",
    label: "Active"
  },
  lost: {
    bg: "bg-[#F3F4F6]",
    text: "text-[#374151]",
    border: "border-[#E5E7EB]",
    label: "Lost"
  },
  red_flag: {
    bg: "bg-[#F3F4F6]",
    text: "text-[#374151]",
    border: "border-[#E5E7EB]",
    label: "Red Flag"
  },
  complaint: {
    bg: "bg-[#FFE4E6]",
    text: "text-[#9F1239]",
    border: "border-[#FECDD3]",
    label: "Complaint"
  },
  open: {
    bg: "bg-[#FFE4E6]",
    text: "text-[#9F1239]",
    border: "border-[#FECDD3]",
    label: "Open"
  },
  in_progress: {
    bg: "bg-[#FFEDD5]",
    text: "text-[#9A3412]",
    border: "border-[#FED7AA]",
    label: "In Progress"
  },
  resolved: {
    bg: "bg-[#D1FAE5]",
    text: "text-[#065F46]",
    border: "border-[#A7F3D0]",
    label: "Resolved"
  },
  closed: {
    bg: "bg-[#F3F4F6]",
    text: "text-[#374151]",
    border: "border-[#E5E7EB]",
    label: "Closed"
  },
  scheduled: {
    bg: "bg-[#DBEAFE]",
    text: "text-[#1E40AF]",
    border: "border-[#BFDBFE]",
    label: "Scheduled"
  },
  completed: {
    bg: "bg-[#D1FAE5]",
    text: "text-[#065F46]",
    border: "border-[#A7F3D0]",
    label: "Completed"
  },
  cancelled: {
    bg: "bg-[#F3F4F6]",
    text: "text-[#374151]",
    border: "border-[#E5E7EB]",
    label: "Cancelled"
  }
};

export const getStatusStyle = (status) => {
  return statusColors[status] || statusColors.new_lead;
};

// Format phone number for display
export const formatPhone = (phone) => {
  if (!phone) return "";
  // Remove non-digits
  const cleaned = phone.replace(/\D/g, "");
  // Format as needed (Indian format)
  if (cleaned.length === 10) {
    return `${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
  }
  return phone;
};

// Format date for display
export const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
};

// Format datetime for display
export const formatDateTime = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

// Format time only
export const formatTime = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit"
  });
};

// Format currency (INR)
export const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return "₹0";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

// Generate WhatsApp link
export const getWhatsAppLink = (phone, message = "") => {
  const cleaned = phone?.replace(/\D/g, "") || "";
  const phoneWithCountry = cleaned.startsWith("91") ? cleaned : `91${cleaned}`;
  const encodedMessage = encodeURIComponent(message);
  return `https://wa.me/${phoneWithCountry}${message ? `?text=${encodedMessage}` : ""}`;
};

// Generate tel: link
export const getTelLink = (phone) => {
  const cleaned = phone?.replace(/\D/g, "") || "";
  return `tel:${cleaned}`;
};

// Call outcome options
export const callOutcomes = [
  { value: "answered", label: "Answered" },
  { value: "no_answer", label: "No Answer" },
  { value: "busy", label: "Busy" },
  { value: "voicemail", label: "Voicemail" },
  { value: "wrong_number", label: "Wrong Number" },
  { value: "callback_requested", label: "Callback Requested" }
];

// Lead sources
export const leadSources = [
  { value: "meta_ads", label: "Meta Ads" },
  { value: "google_ads", label: "Google Ads" },
  { value: "website", label: "Website" },
  { value: "referral", label: "Referral" },
  { value: "walk_in", label: "Walk-in" },
  { value: "other", label: "Other" }
];

// Priority options
export const priorityOptions = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" }
];

// API helper
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const apiRequest = async (endpoint, options = {}) => {
  const token = localStorage.getItem("token");
  const headers = {
    "Content-Type": "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers
  };

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Request failed" }));
    throw new Error(error.detail || "Request failed");
  }

  // Check if response is JSON
  const contentType = response.headers.get("content-type");
  if (contentType && contentType.includes("application/json")) {
    return response.json();
  }
  
  return response;
};
